/**
 * StrategyVersionSwitcher — strategy-canvas version dropdown (Spec A / D51, A6).
 *
 * Mirrors the onboarding editor's version dropdown
 * (`OnboardingEditorActionsV2` version Button+Menu) on the STRATEGY canvas:
 * a lifecycle status chip + a menu listing every baked version. Selecting a
 * version opens a read-only snapshot view (with cherry-pick into the draft);
 * "Compare versions" opens the two-version diff.
 *
 * Lifecycle *actions* (Bake / Start new version) live in the canvas Actions menu
 * per SI-1 — this component is inspection + navigation only. Both are driven by
 * the same `useStrategyVersionActions` hook instance.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Snackbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HistoryIcon from '@mui/icons-material/History';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { IconButton, ListItemText, Tooltip } from '@mui/material';
import type { StrategyVersion, StrategyStatus } from '@segmently/schema-models/strategy';
import type { UseStrategyVersionActionsReturn } from '@modules/strategy-blocks/hooks/useStrategyVersionActions';
import { StrategyVersionViewDialog } from './StrategyVersionViewDialog';
import { StrategyVersionDiffDialog } from './StrategyVersionDiffDialog';

export interface StrategyVersionSwitcherProps {
  actions: UseStrategyVersionActionsReturn;
  /**
   * Fired after an operation rewrites the working head's content (cherry-pick,
   * branch restore). The blocks canvas has no live listener — the render site
   * should reload/remount the canvas (key-prop idiom) so the new content shows.
   */
  onHeadContentChanged?: () => void;
}

const STATUS_META: Record<StrategyStatus, { label: string; color: 'default' | 'info' | 'success' | 'warning' }> = {
  draft: { label: 'draft', color: 'warning' },
  baked: { label: 'baked', color: 'info' },
  published: { label: 'published', color: 'success' },
  archived: { label: 'archived', color: 'default' },
};

export const StrategyVersionSwitcher: React.FC<StrategyVersionSwitcherProps> = ({
  actions,
  onHeadContentChanged,
}) => {
  const {
    versions,
    status,
    currentVersion,
    loading,
    error,
    clearError,
    successMessage,
    clearSuccess,
    getSnapshot,
    cherryPickBlock,
    startNewVersion,
  } = actions;

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [viewVersion, setViewVersion] = useState<StrategyVersion | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);

  const statusMeta = STATUS_META[status] ?? STATUS_META.draft;
  const currentVersionName = versions.find((v) => v.versionId === currentVersion)?.name;
  const buttonLabel = currentVersionName ? `${statusMeta.label} · ${currentVersionName}` : statusMeta.label;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  const handleSelectVersion = (version: StrategyVersion) => {
    handleCloseMenu();
    setViewVersion(version);
  };

  const handleOpenDiff = () => {
    handleCloseMenu();
    setDiffOpen(true);
  };

  const handleCherryPick = async (versionId: string, blockId: string) => {
    const newBlockId = await cherryPickBlock(versionId, blockId);
    // Keep the view open; the working draft now carries the new block. The
    // canvas has no live listener — tell the render site to reload it.
    if (newBlockId) onHeadContentChanged?.();
  };

  // U8 (Spec D2): per-row Fork — start a new draft branched from THAT node.
  // Branching from a non-latest node restores its snapshot into the head.
  const handleFork = async (event: React.MouseEvent, version: StrategyVersion) => {
    event.stopPropagation();
    handleCloseMenu();
    await startNewVersion(version.versionId);
    onHeadContentChanged?.();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<HistoryIcon fontSize="small" />}
        endIcon={<ExpandMoreIcon />}
        onClick={handleOpenMenu}
        disabled={loading}
        data-testid="strategy-version-switcher-btn"
        sx={{
          minWidth: 'auto',
          maxWidth: 190,
          px: 1.5,
          py: 0.5,
          fontSize: '0.8125rem',
          textTransform: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {buttonLabel}
        </Box>
      </Button>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        data-testid="strategy-version-switcher-menu"
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Chip size="small" color={statusMeta.color} label={statusMeta.label} sx={{ mr: 1 }} />
          {versions.length} version{versions.length === 1 ? '' : 's'}
        </MenuItem>
        {versions.length >= 2 && (
          <MenuItem onClick={handleOpenDiff} data-testid="strategy-version-compare">
            <CompareArrowsIcon sx={{ mr: 1, fontSize: 20 }} />
            Compare versions…
          </MenuItem>
        )}
        {versions.length > 0 && <Divider />}
        {versions.length === 0 && (
          <MenuItem disabled>No baked versions yet — Bake from Actions</MenuItem>
        )}
        {versions.map((version) => (
          <MenuItem
            key={version.versionId}
            onClick={() => handleSelectVersion(version)}
            selected={version.versionId === currentVersion}
            data-testid={`strategy-version-item-${version.versionId}`}
            sx={{ pr: 1 }}
          >
            {/* U8: name + operator description, per-row Fork on the right. */}
            <ListItemText
              primary={`${version.name ?? version.versionId}${version.bakedAt ? ` (${new Date(version.bakedAt).toLocaleDateString()})` : ''}`}
              secondary={(version as { description?: string }).description || undefined}
              secondaryTypographyProps={{ noWrap: true, sx: { maxWidth: 260 } }}
            />
            <Tooltip title="Fork an editable copy from this snapshot">
              <IconButton
                size="small"
                edge="end"
                sx={{ ml: 1 }}
                disabled={loading}
                onClick={(event) => void handleFork(event, version)}
                data-testid={`strategy-version-fork-${version.versionId}`}
              >
                <CallSplitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MenuItem>
        ))}
      </Menu>

      <StrategyVersionViewDialog
        open={Boolean(viewVersion)}
        onClose={() => setViewVersion(null)}
        version={viewVersion}
        getSnapshot={getSnapshot}
        onCherryPick={handleCherryPick}
        cherryPickDisabled={loading}
      />

      <StrategyVersionDiffDialog
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        versions={versions}
        getSnapshot={getSnapshot}
      />

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={clearError}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={clearSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={clearSuccess}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StrategyVersionSwitcher;
